// Component: DataTable.jsx
// Purpose: DS DataTable primitives — styled, composable table building blocks
//          (.ds-table): sticky hairline header, no zebra, row hover/selected,
//          tabular-nums, dense mode. Styling only — pages keep their own data,
//          sort, selection and virtualization logic. Compound API:
//          <DataTable><DataTable.Head/><DataTable.Body/>… Row/HeaderCell/Cell.

import React, { forwardRef } from "react";
import { cx } from "./cx";

// --> tiny chevron for sortable headers (rotates via .ds-th-sort.desc)
const SortChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const DataTable = forwardRef(function DataTable(
  { dense = false, className, children, ...props },
  ref
) {
  return (
    <table ref={ref} className={cx("ds-table", dense && "dense", className)} {...props}>
      {children}
    </table>
  );
});

const Head = ({ className, ...p }) => <thead className={className} {...p} />;
const Body = ({ className, ...p }) => <tbody className={className} {...p} />;

const Row = ({ active = false, className, ...p }) => (
  <tr className={cx(active && "is-selected", className)} {...p} />
);

const HeaderCell = ({
  align = "left",
  numeric = false,
  sortable = false,
  sortDir, // "asc" | "desc" | undefined
  onSort,
  className,
  children,
  ...p
}) => (
  <th className={cx((numeric || align === "right") && "num", align === "center" && "center", className)} {...p}>
    {sortable ? (
      <span
        className={cx("ds-th-sort", sortDir && "active", sortDir === "desc" && "desc")}
        onClick={onSort}
        role={onSort ? "button" : undefined}
        tabIndex={onSort ? 0 : undefined}
      >
        {children}
        <SortChevron />
      </span>
    ) : (
      children
    )}
  </th>
);

const Cell = ({ align, numeric = false, muted = false, className, ...p }) => (
  <td
    className={cx(
      (numeric || align === "right") && "num",
      align === "center" && "center",
      muted && "is-muted",
      className
    )}
    {...p}
  />
);

DataTable.Head = Head;
DataTable.Body = Body;
DataTable.Row = Row;
DataTable.HeaderCell = HeaderCell;
DataTable.Cell = Cell;

export default DataTable;
